import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Badge,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { timeAbsenceRequestAPI, apiClient, hrVacationAPI } from '../services/api';
import { VacationBalanceSummary } from '../types';
import moment from 'moment-timezone';

interface TimeAbsenceRequest {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  type: string;
  status: 'pending' | 'in_progress' | 'approved' | 'rejected' | 'cancelled';
  reason?: string;
  approvedByOperations?: string;
  approvedByAdministration?: string;
  approvedByOperationsAt?: string;
  approvedByAdministrationAt?: string;
  operationsReason?: string;
  administrationReason?: string;
  timezone: string;
  resubmissionCount?: number;
  resubmittedAt?: string;
  reminderCount?: number;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
    department?: {
      name: string;
    };
  };
  approvedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  operationsApprover?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  administrationApprover?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const TimeAbsenceRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [vacationBalanceCardEnabled, setVacationBalanceCardEnabled] = useState(true); // Default to enabled
  const [requests, setRequests] = useState<TimeAbsenceRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Sorting state
  const [sortField, setSortField] = useState<'employee' | 'type' | 'dateRange' | 'status' | 'created'>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Vacation balance state
  const [vacationBalance, setVacationBalance] = useState<VacationBalanceSummary | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  // Dialog states
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TimeAbsenceRequest | null>(null);
  
  // Form states
  const [requestForm, setRequestForm] = useState({
    startDate: '',
    endDate: '',
    type: '',
    reason: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    specifyTimes: false // Default to all day (unchecked = all day, checked = specify times)
  });
  
  // Workdays state: array of day numbers (0=Sunday, 1=Monday, ..., 6=Saturday)
  const [workdays, setWorkdays] = useState<number[]>([]);
  const [loadingWorkdays, setLoadingWorkdays] = useState(false);
  
  const [approvalForm, setApprovalForm] = useState({
    action: 'approve',
    reason: ''
  });
  
  const [rejectForm, setRejectForm] = useState({
    reason: '',
    department: 'operations'
  });

  // Limited absence types for Time Absence Requests screen only
  const absenceTypes = [
    'vacation',
    'personal'
  ];

  const vacationAvailableDays = vacationBalance?.availableDays ?? null;
  
  // Debug logging for vacation balance state
  useEffect(() => {
    console.log('🔍 Vacation Balance State Update:', {
      vacationBalance,
      availableDays: vacationBalance?.availableDays,
      accruedDays: vacationBalance?.accruedDays,
      usedDays: vacationBalance?.usedDays,
      vacationAvailableDays,
      timestamp: new Date().toISOString()
    });
  }, [vacationBalance, vacationAvailableDays]);

  // Calculate selected day count (only counting workdays)
  const selectedDayCount = useMemo(() => {
    if (!requestForm.startDate || !requestForm.endDate || workdays.length === 0) {
      return null;
    }
    const start = moment(requestForm.startDate);
    const end = moment(requestForm.endDate);
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    let count = 0;
    const current = start.clone();
    
    while (current.isSameOrBefore(end, 'day')) {
      const dayOfWeek = current.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      if (workdays.includes(dayOfWeek)) {
        count++;
      }
      current.add(1, 'day');
    }
    
    return count;
  }, [requestForm.startDate, requestForm.endDate, workdays]);

  const requestedVacationDays = requestForm.type === 'vacation' ? selectedDayCount : null;

  // Calculate projected available days if anniversary is coming up
  const projectedAvailableDays = useMemo(() => {
    if (!vacationBalance?.nextAnniversaryDate || !requestForm.startDate) {
      return null;
    }

    const today = moment();
    const anniversary = moment(vacationBalance.nextAnniversaryDate);
    const requestStartDate = moment(requestForm.startDate);
    
    // Allow projected days when request starts on/after anniversary
    const isRequestOnOrAfterAnniversary = requestStartDate.isSameOrAfter(anniversary, 'day');
    
    if (!isRequestOnOrAfterAnniversary) {
      return null; // Not eligible for projected days
    }

    // Calculate projected days
    // On anniversary, employees typically get their full annual allocation
    // We need to estimate what they'll have available by anniversary
    
    // Strategy: Estimate annual allocation based on accrued days
    // If they've accrued X days so far, estimate annual allocation conservatively
    // Common accrual rates: 10-15 days for first year, 15-20 for 2-5 years, 20+ for 5+ years
    
    // Calculate days since last anniversary (approximate)
    const lastAnniversary = anniversary.clone().subtract(1, 'year');
    const daysSinceLastAnniversary = today.diff(lastAnniversary, 'days');
    
    // Estimate annual allocation using HR policy table (years of service -> days)
    const getAnnualAllocation = (yearsOfService: number): number => {
      if (yearsOfService < 1) return 0; // 0-1 years
      if (yearsOfService < 2) return 12; // 1 year
      if (yearsOfService < 3) return 14; // 2 years
      if (yearsOfService < 4) return 16; // 3 years
      if (yearsOfService < 5) return 18; // 4 years
      if (yearsOfService < 6) return 20; // 5 years
      if (yearsOfService <= 10) return 22; // 6-10 years
      if (yearsOfService <= 15) return 24; // 11-15 years
      if (yearsOfService <= 20) return 26; // 16-20 years
      return 28; // 21+ years
    };

    // Use years of service at the upcoming anniversary (current + 1)
    const yearsOfServiceAtAnniversary = (vacationBalance.yearsOfService || 0) + 1;
    let estimatedAnnualAllocation = getAnnualAllocation(yearsOfServiceAtAnniversary);
    // Safety: never estimate less than already accrued
    estimatedAnnualAllocation = Math.max(estimatedAnnualAllocation, vacationBalance.accruedDays || 0);
    
    // Projected available days = (estimated annual allocation - used days) + carryover days
    // Or: current available + (new days that will accrue by anniversary)
    const usedDays = vacationBalance.usedDays || 0;
    const carryoverDays = vacationBalance.carryoverDays || 0;
    const newDaysOnAnniversary = estimatedAnnualAllocation - vacationBalance.accruedDays;
    
    // Projected = what they'll have after anniversary
    const projected = Math.max(
      (estimatedAnnualAllocation - usedDays) + carryoverDays,
      (vacationBalance.availableDays || 0) + newDaysOnAnniversary
    );
    
    return projected;
  }, [vacationBalance, requestForm.startDate, requestForm.type]);

  const exceedsVacationAllowance = useMemo(() => {
    if (requestedVacationDays === null || vacationAvailableDays === null) {
      return false;
    }
    
    // Allow if current days are sufficient
    if (requestedVacationDays <= vacationAvailableDays) {
      return false;
    }
    
    // Check if we can use projected days
    if (projectedAvailableDays !== null && requestedVacationDays <= projectedAvailableDays) {
      return false; // Projected days are sufficient
    }
    
    return true; // Exceeds both current and projected
  }, [requestedVacationDays, vacationAvailableDays, projectedAvailableDays]);

  const hasVacationDaysAvailable = vacationAvailableDays !== null && vacationAvailableDays > 0;
  
  // Check if request qualifies for projected days (30+ days before anniversary, request on/after anniversary)
  const canUseProjectedDays = useMemo(() => {
    if (!vacationBalance?.nextAnniversaryDate || !requestForm.startDate || requestForm.type !== 'vacation') {
      return false;
    }
    
    const today = moment();
    const anniversary = moment(vacationBalance.nextAnniversaryDate);
    const requestStartDate = moment(requestForm.startDate);
    
    const daysUntilAnniversary = anniversary.diff(today, 'days');
    const isRequestOnOrAfterAnniversary = requestStartDate.isSameOrAfter(anniversary, 'day');
    
    return isRequestOnOrAfterAnniversary;
  }, [vacationBalance, requestForm.startDate, requestForm.type]);

  const canUseProjectedDaysForSelection = useMemo(() => {
    if (!vacationBalance?.nextAnniversaryDate || !requestForm.startDate) {
      return false;
    }
    const today = moment();
    const anniversary = moment(vacationBalance.nextAnniversaryDate);
    const requestStartDate = moment(requestForm.startDate);
    const daysUntilAnniversary = anniversary.diff(today, 'days');
    const isRequestOnOrAfterAnniversary = requestStartDate.isSameOrAfter(anniversary, 'day');
    return isRequestOnOrAfterAnniversary;
  }, [vacationBalance, requestForm.startDate]);

  const isVacationSelectable = useMemo(() => {
    if (vacationAvailableDays === null) {
      return false;
    }
    if (vacationAvailableDays > 0) {
      return true;
    }
    return canUseProjectedDaysForSelection && projectedAvailableDays !== null && projectedAvailableDays > 0;
  }, [vacationAvailableDays, canUseProjectedDaysForSelection, projectedAvailableDays]);

  useEffect(() => {
    if (requestForm.type === 'vacation' && !isVacationSelectable) {
      setRequestForm((prev) => ({ ...prev, type: '' }));
    }
  }, [requestForm.type, isVacationSelectable]);

  const isPolicyAdvanceWarning = useMemo(() => {
    if (!vacationBalance?.nextAnniversaryDate || !requestForm.startDate || requestForm.type !== 'vacation') {
      return false;
    }
    const today = moment();
    const requestStartDate = moment(requestForm.startDate);
    const daysUntilRequest = requestStartDate.diff(today, 'days');
    return daysUntilRequest >= 0 && daysUntilRequest < 30;
  }, [vacationBalance, requestForm.startDate, requestForm.type]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning'; // Orange - needs Administration approval
      case 'in_progress': return 'error'; // Red - Administration approved, needs Operations approval
      case 'approved': return 'success'; // Green - fully approved
      case 'rejected': return 'error'; // Red - rejected
      case 'cancelled': return 'default'; // Gray - cancelled
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in_progress': return 'In Progress';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const loadMyRequests = async () => {
    try {
      setLoading(true);
      const response = await timeAbsenceRequestAPI.getMyRequests();
      setRequests(response.data.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await timeAbsenceRequestAPI.getPending();
      setRequests(response.data.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedRequests = async () => {
    try {
      setLoading(true);
      const response = await timeAbsenceRequestAPI.getApproved();
      setRequests(response.data.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load approved requests');
    } finally {
      setLoading(false);
    }
  };

  // Load vacation balance card setting
  useEffect(() => {
    const loadVacationBalanceCardSetting = async () => {
      try {
        const response = await apiClient.globalSettings.get('vacation_balance_card_enabled');
        if (response.data.success) {
          const value = response.data.data?.value;
          if (value !== undefined) {
            setVacationBalanceCardEnabled(value);
          } else if (typeof value === 'string') {
            setVacationBalanceCardEnabled(value === 'true' || value === '1');
          } else {
            setVacationBalanceCardEnabled(true); // Default to enabled
          }
        }
      } catch (error) {
        console.error('Error loading vacation balance card setting:', error);
        setVacationBalanceCardEnabled(true); // Default to enabled on error
      }
    };
    loadVacationBalanceCardSetting();
  }, []);

  // Load vacation balance when component mounts or user changes
  useEffect(() => {
    // Only load vacation balance if the card is enabled
    if (!vacationBalanceCardEnabled) {
      return;
    }

    const loadVacationBalance = async () => {
      if (!user?.employeeId) {
        console.warn('No employee ID found for user. Vacation balance cannot be loaded.');
        console.log('User object:', user);
        return;
      }
      
      console.log('🔍 Loading Vacation Balance - User Info:', {
        userId: user.id,
        employeeId: user.employeeId,
        employeeIdType: typeof user.employeeId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        fullUserObject: user
      });
      
      // Validate employeeId is a numeric string before making API call
      const numericId = parseInt(user.employeeId, 10);
      if (isNaN(numericId)) {
        console.warn(`❌ Invalid employee ID format: "${user.employeeId}". Expected a numeric string (e.g., "001234" or "1234").`);
        console.log('User employeeId value:', user.employeeId);
        return;
      }
      
      console.log('🔍 Employee ID Conversion:', {
        original: user.employeeId,
        numericId: numericId,
        willCallAPIWith: numericId.toString()
      });
      
      try {
        setLoadingBalance(true);
        // Clear old state first to ensure fresh data
        setVacationBalance(null);
        
        const response = await hrVacationAPI.getBalance();
        console.log('✅ Vacation Balance API Response (Component):', {
          requestedEmployeeId: user.employeeId,
          responseEmployeeId: response.data?.employeeId,
          employeeIdMatch: user.employeeId === response.data?.employeeId?.toString().padStart(6, '0'),
          responseType: typeof response,
          responseData: response.data,
          responseDataType: typeof response.data,
          isObject: response.data && typeof response.data === 'object',
          availableDays: response.data?.availableDays,
          availableDaysType: typeof response.data?.availableDays,
          accruedDays: response.data?.accruedDays,
          usedDays: response.data?.usedDays,
          usedDaysCompleted: response.data?.usedDaysCompleted,
          usedDaysApproved: response.data?.usedDaysApproved,
          carryoverDays: response.data?.carryoverDays,
          fullResponse: JSON.stringify(response.data, null, 2),
          responseKeys: response.data ? Object.keys(response.data) : 'no data',
          timestamp: new Date().toISOString()
        });
        
        // Verify the data before setting state
        if (response.data && typeof response.data === 'object') {
          // Validate all required fields
          const hasValidData = 
            typeof response.data.availableDays === 'number' &&
            typeof response.data.accruedDays === 'number' &&
            typeof response.data.usedDays === 'number';
          
          if (hasValidData) {
            console.log('✅ Setting vacation balance state:', {
              availableDays: response.data.availableDays,
              accruedDays: response.data.accruedDays,
              usedDays: response.data.usedDays,
              usedDaysCompleted: response.data.usedDaysCompleted,
              usedDaysApproved: response.data.usedDaysApproved,
              carryoverDays: response.data.carryoverDays,
              fullObject: response.data
            });
            // Ensure we're setting the complete object, not just a property
            setVacationBalance({
              ...response.data,
              employeeId: response.data.employeeId || parseInt(user.employeeId, 10),
              availableDays: response.data.availableDays,
              accruedDays: response.data.accruedDays,
              usedDays: response.data.usedDays,
              usedDaysCompleted: response.data.usedDaysCompleted || 0,
              usedDaysApproved: response.data.usedDaysApproved || 0,
              carryoverDays: response.data.carryoverDays || 0,
              asOfDate: response.data.asOfDate,
              yearsOfService: response.data.yearsOfService || 0,
              nextAnniversaryDate: response.data.nextAnniversaryDate,
              status: response.data.status || 'OK'
            });
          } else {
            console.error('❌ Invalid response data structure - missing required fields:', {
              hasAvailableDays: typeof response.data.availableDays === 'number',
              hasAccruedDays: typeof response.data.accruedDays === 'number',
              hasUsedDays: typeof response.data.usedDays === 'number',
              responseData: response.data
            });
            setVacationBalance(null);
          }
        } else {
          console.error('❌ Invalid response - data is not an object:', {
            responseType: typeof response.data,
            responseData: response.data
          });
          setVacationBalance(null);
        }
      } catch (error: any) {
        // Log error for debugging
        console.error('❌ Failed to load vacation balance:', error.message || error);
        console.error('Error details:', {
          employeeId: user.employeeId,
          error: error,
          response: error.response?.data
        });
        setVacationBalance(null);
      } finally {
        setLoadingBalance(false);
      }
    };

    loadVacationBalance();
    
    // Cleanup: Clear balance when component unmounts or user changes
    return () => {
      setVacationBalance(null);
    };
  }, [user?.employeeId, vacationBalanceCardEnabled]);

  useEffect(() => {
    if (activeTab === 0) {
      loadMyRequests();
    } else if (activeTab === 1) {
      loadPendingRequests();
    } else if (activeTab === 2) {
      loadApprovedRequests();
    }
  }, [activeTab]);

  // Handle sort
  const handleSort = (field: 'employee' | 'type' | 'dateRange' | 'status' | 'created') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Sort requests
  const sortedRequests = [...requests].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'employee':
        aValue = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
        bValue = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase();
        break;
      case 'type':
        aValue = a.type.toLowerCase();
        bValue = b.type.toLowerCase();
        break;
      case 'dateRange':
        aValue = new Date(a.startDate).getTime();
        bValue = new Date(b.startDate).getTime();
        break;
      case 'status':
        aValue = a.status.toLowerCase();
        bValue = b.status.toLowerCase();
        break;
      case 'created':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Load workdays from user's schedule
  const loadWorkdaysFromSchedule = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingWorkdays(true);
      // Get schedule for the next 6 weeks to get a better sample of workdays
      const startDate = moment().format('YYYY-MM-DD');
      const endDate = moment().add(6, 'weeks').format('YYYY-MM-DD');
      
      const response = await apiClient.schedules.getMyScheduleRange(startDate, endDate);
      const schedules = response.data.data || [];
      
      console.log('📅 Loading workdays from schedule:', {
        scheduleCount: schedules.length,
        dateRange: `${startDate} to ${endDate}`
      });
      
      // Extract unique days of week from schedules
      const daysSet = new Set<number>();
      schedules.forEach((schedule: any) => {
        if (schedule.date) {
          const dayOfWeek = moment(schedule.date).day(); // 0=Sunday, 1=Monday, etc.
          daysSet.add(dayOfWeek);
        }
      });
      
      const foundDays = Array.from(daysSet).sort();
      console.log('📅 Found workdays from schedules:', foundDays);
      
      // If we found schedules, use those days
      if (foundDays.length > 0) {
        // Check if the pattern looks like M-F (has Mon, Tue, Wed, Thu, Fri)
        const hasMonday = foundDays.includes(1);
        const hasTuesday = foundDays.includes(2);
        const hasWednesday = foundDays.includes(3);
        const hasThursday = foundDays.includes(4);
        const hasFriday = foundDays.includes(5);
        
        // If we have Mon-Fri pattern, default to full M-F even if some days are missing in the sample
        if (hasMonday && hasTuesday && hasWednesday && hasThursday && hasFriday) {
          console.log('✅ Detected M-F pattern, using full M-F workdays');
          setWorkdays([1, 2, 3, 4, 5]); // Monday through Friday
        } else if (foundDays.length >= 3) {
          // If we have at least 3 different days, use what we found
          console.log('✅ Using found workdays:', foundDays);
          setWorkdays(foundDays);
        } else {
          // If we have very few days (1-2), it might be incomplete data
          // Check localStorage first, then default to M-F
          console.log('⚠️ Few workdays found, checking localStorage or defaulting to M-F');
          const savedWorkdays = localStorage.getItem('teks-time-workdays-preference');
          if (savedWorkdays) {
            try {
              const parsed = JSON.parse(savedWorkdays);
              if (Array.isArray(parsed) && parsed.length >= 3) {
                console.log('✅ Using saved workdays preference:', parsed);
                setWorkdays(parsed);
              } else {
                // Default to M-F if saved preference is invalid
                console.log('✅ Defaulting to M-F (saved preference invalid)');
                setWorkdays([1, 2, 3, 4, 5]);
              }
            } catch {
              console.log('✅ Defaulting to M-F (parse error)');
              setWorkdays([1, 2, 3, 4, 5]);
            }
          } else {
            // Default to M-F for typical work schedule
            console.log('✅ Defaulting to M-F (no saved preference)');
            setWorkdays([1, 2, 3, 4, 5]);
          }
        }
      } else {
        // Try to load from localStorage
        const savedWorkdays = localStorage.getItem('teks-time-workdays-preference');
        if (savedWorkdays) {
          try {
            const parsed = JSON.parse(savedWorkdays);
            if (Array.isArray(parsed) && parsed.length > 0) {
              console.log('✅ Using saved workdays preference:', parsed);
              setWorkdays(parsed);
            } else {
              // Default to M-F if no preference
              console.log('✅ Defaulting to M-F (saved preference empty)');
              setWorkdays([1, 2, 3, 4, 5]);
            }
          } catch {
            console.log('✅ Defaulting to M-F (parse error)');
            setWorkdays([1, 2, 3, 4, 5]); // Default to M-F
          }
        } else {
          // Default to M-F if no schedule and no preference
          console.log('✅ Defaulting to M-F (no schedule, no preference)');
          setWorkdays([1, 2, 3, 4, 5]);
        }
      }
    } catch (error) {
      console.warn('❌ Failed to load workdays from schedule:', error);
      // Default to M-F on error
      const savedWorkdays = localStorage.getItem('teks-time-workdays-preference');
      if (savedWorkdays) {
        try {
          const parsed = JSON.parse(savedWorkdays);
          if (Array.isArray(parsed) && parsed.length > 0) {
            console.log('✅ Using saved workdays preference (error fallback):', parsed);
            setWorkdays(parsed);
          } else {
            console.log('✅ Defaulting to M-F (error fallback, invalid preference)');
            setWorkdays([1, 2, 3, 4, 5]);
          }
        } catch {
          console.log('✅ Defaulting to M-F (error fallback, parse error)');
          setWorkdays([1, 2, 3, 4, 5]);
        }
      } else {
        console.log('✅ Defaulting to M-F (error fallback, no preference)');
        setWorkdays([1, 2, 3, 4, 5]);
      }
    } finally {
      setLoadingWorkdays(false);
    }
  };

  const handleCreateRequest = async () => {
    try {
      setLoading(true);
      
      // Validate vacation days if requesting vacation
      if (requestForm.type === 'vacation') {
        if (vacationAvailableDays === null) {
          setError('Vacation balance information is unavailable. Please contact your supervisor.');
          setLoading(false);
          return;
        }

        if (!requestedVacationDays || requestedVacationDays <= 0) {
          setError('Please select a valid date range for your vacation request.');
          setLoading(false);
          return;
        }

        // Allow if current days are sufficient (anytime)
        if (requestedVacationDays <= vacationAvailableDays) {
          // Valid - proceed with request
        }
        // Or allow if 30+ days before anniversary and request is on/after anniversary with projected days
        else if (canUseProjectedDays && projectedAvailableDays !== null && requestedVacationDays <= projectedAvailableDays) {
          // Valid - using projected days - proceed with request
        }
        // Otherwise, block the request
        else {
          if (canUseProjectedDays && projectedAvailableDays !== null) {
            setError(
              `You have ${vacationAvailableDays.toFixed(1)} vacation days available now. ` +
              `By your anniversary (${moment(vacationBalance?.nextAnniversaryDate).format('MMM DD, YYYY')}), ` +
              `you are projected to have approximately ${projectedAvailableDays.toFixed(1)} days available. ` +
              `You requested ${requestedVacationDays} days. Please adjust your date range.`
            );
          } else {
            setError(
              `You only have ${vacationAvailableDays.toFixed(1)} vacation days available. ` +
              `You requested ${requestedVacationDays} days. Please adjust your date range.`
            );
          }
          setLoading(false);
          return;
        }
      }
      
      // Prepare request data
      let requestData = { ...requestForm };
      
      // If all day (specifyTimes = false), convert date-only to datetime with start/end of day in user's timezone
      if (!requestForm.specifyTimes) {
        const start = moment.tz(requestForm.startDate, 'YYYY-MM-DD', requestForm.timezone).startOf('day').toISOString();
        const end = moment.tz(requestForm.endDate, 'YYYY-MM-DD', requestForm.timezone).endOf('day').toISOString();
        requestData = {
          ...requestForm,
          startDate: start,
          endDate: end
        };
      } else {
        // For datetime-local (specifyTimes = true), browser interprets it in local timezone, then convert to ISO
        // datetime-local format: "YYYY-MM-DDTHH:mm" (interpreted as browser's local timezone)
        const start = moment(requestForm.startDate).toISOString();
        const end = moment(requestForm.endDate).toISOString();
        requestData = {
          ...requestForm,
          startDate: start,
          endDate: end
        };
      }
      
      // Remove specifyTimes from request data (backend doesn't need it)
      const { specifyTimes, ...submitData } = requestData;
      
      // Include workdays in the request
      await timeAbsenceRequestAPI.create({
        ...submitData,
        workdays: workdays
      });
      setSuccess('Time absence request submitted successfully');
      setRequestDialogOpen(false);
      setRequestForm({
        startDate: '',
        endDate: '',
        type: '',
        reason: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        specifyTimes: false // Reset to default (all day)
      });
      // Save workdays preference to localStorage
      if (workdays.length > 0) {
        localStorage.setItem('teks-time-workdays-preference', JSON.stringify(workdays));
      }
      loadMyRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest || !user) return;
    
    // Require reason for both approve and reject actions
    if (!approvalForm.reason || !approvalForm.reason.trim()) {
      setError(`Reason is required to ${approvalForm.action} this request`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Let the backend determine the department based on the user's role and configured approvers
      // The backend will check if the user is authorized to approve for any department
      const approvalData = {
        ...approvalForm,
        department: 'auto' // Let backend determine the department
      };
      
      await timeAbsenceRequestAPI.approve(selectedRequest.id, approvalData);
      setSuccess(`Request ${approvalForm.action}d successfully`);
      setApprovalDialogOpen(false);
      setSelectedRequest(null);
      setApprovalForm({ action: 'approve', reason: '' }); // Reset form
      loadPendingRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      setLoading(true);
      await timeAbsenceRequestAPI.cancel(requestId);
      setSuccess('Request cancelled successfully');
      loadMyRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel request');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to delete this cancelled request? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await timeAbsenceRequestAPI.delete(requestId);
      setSuccess('Request deleted successfully');
      loadMyRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete request');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (requestId: string) => {
    try {
      setLoading(true);
      const response = await timeAbsenceRequestAPI.sendReminder(requestId);
      setSuccess(response.data.message || 'Reminder sent successfully');
      // Reload requests to update reminder count
      loadMyRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to send reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    try {
      setLoading(true);
      await timeAbsenceRequestAPI.reject(selectedRequest.id, rejectForm);
      setSuccess('Request rejected successfully');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      setRejectForm({ reason: '', department: 'operations' });
      
      // Reload the appropriate tab
      if (activeTab === 2) {
        loadApprovedRequests();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalDialog = (request: TimeAbsenceRequest, action: 'approve' | 'reject' = 'approve') => {
    setSelectedRequest(request);
    setApprovalForm({ action, reason: '' }); // Reset form with action
    setApprovalDialogOpen(true);
    setError(null); // Clear any previous errors
  };

  const openRejectDialog = (request: TimeAbsenceRequest) => {
    setSelectedRequest(request);
    setRejectDialogOpen(true);
  };

  const openNotesDialog = (request: TimeAbsenceRequest) => {
    setSelectedRequest(request);
    setNotesDialogOpen(true);
  };

  const openEditDialog = (request: TimeAbsenceRequest) => {
    setSelectedRequest(request);
    // Pre-fill form with existing request data
    const startMoment = moment(request.startDate).tz(request.timezone);
    const endMoment = moment(request.endDate).tz(request.timezone);
    
    // Check if times are specified (not midnight start/end of day)
    const hasSpecificTimes = !(startMoment.hour() === 0 && startMoment.minute() === 0 && 
                                endMoment.hour() === 23 && endMoment.minute() === 59);
    
    setRequestForm({
      startDate: startMoment.format('YYYY-MM-DD'),
      endDate: endMoment.format('YYYY-MM-DD'),
      type: request.type,
      reason: request.reason || '',
      timezone: request.timezone,
      specifyTimes: hasSpecificTimes
    });
    
    if (hasSpecificTimes) {
      setRequestForm(prev => ({
        ...prev,
        startDate: startMoment.format('YYYY-MM-DDTHH:mm'),
        endDate: endMoment.format('YYYY-MM-DDTHH:mm')
      }));
    }
    
    setEditDialogOpen(true);
    setError(null);
  };

  const handleResubmitRequest = async () => {
    if (!selectedRequest) return;

    try {
      setLoading(true);
      
      // Format dates properly based on specifyTimes
      let startDate: string;
      let endDate: string;
      
      if (requestForm.specifyTimes) {
        // Use the date-time values as-is
        startDate = requestForm.startDate;
        endDate = requestForm.endDate;
      } else {
        // Set to start and end of day in the user's timezone
        const startMoment = moment.tz(requestForm.startDate, requestForm.timezone).startOf('day');
        const endMoment = moment.tz(requestForm.endDate, requestForm.timezone).endOf('day');
        startDate = startMoment.format('YYYY-MM-DDTHH:mm:ss');
        endDate = endMoment.format('YYYY-MM-DDTHH:mm:ss');
      }

      await timeAbsenceRequestAPI.update(selectedRequest.id, {
        startDate,
        endDate,
        type: requestForm.type,
        reason: requestForm.reason,
        timezone: requestForm.timezone
      });
      
      setSuccess('Request updated and resubmitted successfully');
      setEditDialogOpen(false);
      setSelectedRequest(null);
      setRequestForm({
        startDate: '',
        endDate: '',
        type: '',
        reason: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        specifyTimes: false
      });
      loadMyRequests();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to resubmit request');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string, timezone: string) => {
    return moment(dateString).tz(timezone).format('MMM DD, YYYY HH:mm');
  };

  const formatDateRange = (startDate: string, endDate: string, timezone: string) => {
    const start = moment(startDate).tz(timezone);
    const end = moment(endDate).tz(timezone);
    
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return `${start.format('MMM DD, YYYY')} ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    } else {
      return `${start.format('MMM DD, YYYY HH:mm')} - ${end.format('MMM DD, YYYY HH:mm')}`;
    }
  };

  const canApprove = user?.role && ['manager', 'administrator', 'super_user'].includes(user.role);

  // Calculate pending count (only show badge when count > 0)
  const pendingCount = requests.filter(
    (req) => req.status === 'pending' || req.status === 'in_progress'
  ).length;

  const renderApprovalProgress = (request: TimeAbsenceRequest) => {
    const administrationStatus = {
      label: 'Administration',
      completed: !!request.approvedByAdministration,
      pending: !request.approvedByAdministration && (request.status === 'pending' || request.status === 'in_progress'),
      approvedAt: request.approvedByAdministrationAt,
      reason: request.administrationReason
    };

    const operationsStatus = {
      label: 'Operations',
      completed: !!request.approvedByOperations,
      pending: !request.approvedByOperations && (request.status === 'in_progress' || request.status === 'approved'),
      approvedAt: request.approvedByOperationsAt,
      reason: request.operationsReason
    };

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Administration Status (First Step) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: administrationStatus.completed 
                ? 'success.main' 
                : administrationStatus.pending 
                  ? 'warning.main' 
                  : 'grey.300',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '8px',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {administrationStatus.completed ? '✓' : administrationStatus.pending ? '⏳' : '○'}
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: administrationStatus.completed ? 'bold' : 'normal',
              color: administrationStatus.completed ? 'success.main' : administrationStatus.pending ? 'warning.main' : 'text.secondary'
            }}
          >
            Administration
          </Typography>
          {administrationStatus.completed && administrationStatus.approvedAt && (
            <Typography variant="caption" color="text.secondary">
              • {moment(administrationStatus.approvedAt).format('MMM DD')}
            </Typography>
          )}
        </Box>

        {/* Operations Status (Second Step) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: operationsStatus.completed 
                ? 'success.main' 
                : operationsStatus.pending 
                  ? 'warning.main' 
                  : 'grey.300',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '8px',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {operationsStatus.completed ? '✓' : operationsStatus.pending ? '⏳' : '○'}
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: operationsStatus.completed ? 'bold' : 'normal',
              color: operationsStatus.completed ? 'success.main' : operationsStatus.pending ? 'warning.main' : 'text.secondary'
            }}
          >
            Operations
          </Typography>
          {operationsStatus.completed && operationsStatus.approvedAt && (
            <Typography variant="caption" color="text.secondary">
              • {moment(operationsStatus.approvedAt).format('MMM DD')}
            </Typography>
          )}
        </Box>

        {/* Approval Details (only show if there are completed approvals) */}
        {(administrationStatus.completed || operationsStatus.completed) && (
          <Box sx={{ mt: 0.5, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            {administrationStatus.completed && (
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                  Administration ✓
                </Typography>
                {administrationStatus.approvedAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {moment(administrationStatus.approvedAt).format('MMM DD, h:mm A')}
                  </Typography>
                )}
                {administrationStatus.reason && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    "{administrationStatus.reason}"
                  </Typography>
                )}
              </Box>
            )}
            {operationsStatus.completed && (
              <Box>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                  Operations ✓
                </Typography>
                {operationsStatus.approvedAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {moment(operationsStatus.approvedAt).format('MMM DD, h:mm A')}
                  </Typography>
                )}
                {operationsStatus.reason && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    "{operationsStatus.reason}"
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" mb={3}>
        Time Absence Requests
      </Typography>

      {/* Vacation Balance Card - Only show if enabled */}
      {vacationBalanceCardEnabled && (
        <>
          {loadingBalance && (
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography>Loading vacation balance...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
          {vacationBalance && (() => {
            // Calculate employee start date from nextAnniversaryDate and yearsOfService
            let employeeStartDate: string | null = null;
            if (vacationBalance.nextAnniversaryDate) {
              const nextAnniversary = moment(vacationBalance.nextAnniversaryDate);
              // Subtract years of service + 1 (since next anniversary is for the upcoming year)
              const startDate = nextAnniversary.clone().subtract(vacationBalance.yearsOfService + 1, 'years');
              employeeStartDate = startDate.format('MMM DD, YYYY');
            }
            
            return (
              <Card 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2,
                  boxShadow: 3,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
                    pointerEvents: 'none'
                  }
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                      🏖️ Vacation Balance
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {/* Vacation Metrics - Left Side */}
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            bgcolor: 'rgba(255,255,255,0.15)', 
                            borderRadius: 1.5, 
                            p: 1.5,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}>
                            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                              Accrued Days
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2, fontSize: '1.25rem' }}>
                              {vacationBalance.accruedDays.toFixed(1)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            bgcolor: 'rgba(255,255,255,0.15)', 
                            borderRadius: 1.5, 
                            p: 1.5,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}>
                            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                              Used Days
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2, fontSize: '1.25rem' }}>
                              {(() => {
                                const usedDays = vacationBalance.usedDays ?? 0;
                                console.log('📊 Displaying Used Days:', {
                                  usedDays,
                                  usedDaysType: typeof usedDays,
                                  usedDaysCompleted: vacationBalance.usedDaysCompleted,
                                  usedDaysApproved: vacationBalance.usedDaysApproved,
                                  fullBalance: vacationBalance
                                });
                                return usedDays.toFixed(1);
                              })()}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            bgcolor: 'rgba(76, 175, 80, 0.25)', 
                            borderRadius: 1.5, 
                            p: 1.5,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(76, 175, 80, 0.4)'
                          }}>
                            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                              Available Days
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2, fontSize: '1.25rem', color: '#4caf50' }}>
                              {vacationBalance.availableDays.toFixed(1)}
                            </Typography>
                          </Box>
                        </Grid>
                        {vacationBalance.nextAnniversaryDate && (
                          <Grid item xs={12}>
                            <Box sx={{ 
                              bgcolor: 'rgba(255,255,255,0.15)', 
                              borderRadius: 1.5, 
                              p: 1.5,
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                                Next Anniversary
                              </Typography>
                              <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.2, fontSize: '1rem' }}>
                                {moment(vacationBalance.nextAnniversaryDate).format('MMM DD, YYYY')}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    
                    {/* Employee Information - Right Side */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        bgcolor: 'rgba(255,255,255,0.15)', 
                        borderRadius: 1.5, 
                        p: 1.5,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        height: '100%'
                      }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.3, fontSize: '0.8rem' }}>
                              Employee ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {user?.employeeId || vacationBalance.employeeId}
                            </Typography>
                          </Grid>
                          {employeeStartDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.3, fontSize: '0.8rem' }}>
                                Employee Since
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                                {employeeStartDate}
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.3, fontSize: '0.8rem' }}>
                              Years of Service
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {vacationBalance.yearsOfService}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.3, fontSize: '0.8rem' }}>
                              As of
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                              {moment(vacationBalance.asOfDate).format('MMM DD, YYYY')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })()}
        </>
      )}

      {/* Request Time Off Button */}
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={async () => {
            setRequestDialogOpen(true);
            // Load workdays from schedule when dialog opens
            await loadWorkdaysFromSchedule();
          }}
          disabled={!user}
          size="large"
        >
          Request Time Off
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="My Requests" />
            {canApprove && (
              <Tab 
                label={
                  pendingCount > 0 ? (
                    <Badge badgeContent={pendingCount} color="error">
                      Pending Approvals
                    </Badge>
                  ) : (
                    "Pending Approvals"
                  )
                }
              />
            )}
            {canApprove && <Tab label="Approved Requests" />}
          </Tabs>
        </Box>

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {(activeTab === 1 || activeTab === 2) && (
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'employee'}
                        direction={sortField === 'employee' ? sortOrder : 'asc'}
                        onClick={() => handleSort('employee')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'type'}
                      direction={sortField === 'type' ? sortOrder : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'dateRange'}
                      direction={sortField === 'dateRange' ? sortOrder : 'asc'}
                      onClick={() => handleSort('dateRange')}
                    >
                      Date Range
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'created'}
                      direction={sortField === 'created' ? sortOrder : 'asc'}
                      onClick={() => handleSort('created')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  {activeTab === 0 && <TableCell>Actions</TableCell>}
                  {activeTab === 1 && <TableCell>Actions</TableCell>}
                  {activeTab === 2 && <TableCell>Approved By</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRequests.map((request) => (
                  <TableRow key={request.id}>
                    {(activeTab === 1 || activeTab === 2) && (
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {request.user?.firstName} {request.user?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.user?.employeeId} • {request.user?.department?.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={request.type.replace('_', ' ').toUpperCase()}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2">
                            {formatDateRange(request.startDate, request.endDate, request.timezone)}
                          </Typography>
                          {request.resubmissionCount != null && request.resubmissionCount > 0 && (
                            <Tooltip title={`This request was resubmitted ${request.resubmissionCount} time${request.resubmissionCount > 1 ? 's' : ''}${request.resubmittedAt ? ` (Last: ${moment(request.resubmittedAt).format('MMM DD, YYYY')})` : ''}`}>
                              <RefreshIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {request.reason && (
                          <Typography variant="caption" color="text.secondary">
                            {request.reason}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={getStatusLabel(request.status)}
                            color={getStatusColor(request.status) as any}
                            size="small"
                          />
                          {request.status === 'in_progress' && request.approvedByOperations && (
                            <Chip
                              label="Ops Approved"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {request.resubmissionCount != null && request.resubmissionCount > 0 && (
                            <Chip
                              icon={<RefreshIcon />}
                              label={`Resubmitted (Attempt #${request.resubmissionCount})`}
                              color="warning"
                              size="small"
                              variant="outlined"
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#f57c00',
                                '& .MuiChip-icon': {
                                  color: '#f57c00'
                                }
                              }}
                            />
                          )}
                        </Box>
                        {['pending', 'in_progress'].includes(request.status) && (
                          renderApprovalProgress(request)
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {moment(request.createdAt).format('MMM DD, YYYY')}
                    </TableCell>
                    <TableCell>
                      {activeTab === 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {(request.status === 'pending' || request.status === 'in_progress') && 
                           (request.reminderCount === undefined || request.reminderCount < 2) && (
                            <Tooltip title={`Send reminder to ${request.status === 'pending' ? 'Administration' : 'Operations'} approver (${(request.reminderCount || 0)}/2 sent)`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendReminder(request.id)}
                                disabled={loading}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {request.status === 'pending' && (
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelRequest(request.id)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {request.status === 'cancelled' && (
                            <Tooltip title="Delete Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRequest(request.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {request.status === 'rejected' && (
                            <>
                              <Tooltip title="View Rejection Notes">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => openNotesDialog(request)}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit & Resubmit">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openEditDialog(request)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      ) : activeTab === 1 ? (
                        (request.status === 'pending' || request.status === 'in_progress') && (
                          <Box>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openApprovalDialog(request, 'approve')}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openApprovalDialog(request, 'reject')}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      ) : activeTab === 2 ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Operations: {request.operationsApprover?.firstName} {request.operationsApprover?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.approvedByOperationsAt && moment(request.approvedByOperationsAt).format('MMM DD, YYYY')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                            Administration: {request.administrationApprover?.firstName} {request.administrationApprover?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.approvedByAdministrationAt && moment(request.approvedByAdministrationAt).format('MMM DD, YYYY')}
                          </Typography>
                          {canApprove && (
                            <Box sx={{ mt: 1 }}>
                              <Tooltip title="Reject Approved Request">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => openRejectDialog(request)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Time Off</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requestForm.specifyTimes}
                    onChange={(e) => {
                      const specifyTimes = e.target.checked;
                      setRequestForm({ 
                        ...requestForm, 
                        specifyTimes,
                        // Clear dates when switching modes
                        startDate: '',
                        endDate: ''
                      });
                    }}
                  />
                }
                label="Specify Times (Less than a day)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={requestForm.specifyTimes ? "Start Date & Time" : "Start Date"}
                type={requestForm.specifyTimes ? "datetime-local" : "date"}
                value={requestForm.startDate}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  setRequestForm({ ...requestForm, startDate: newStartDate });
                  
                  if (requestForm.type === 'vacation' && hasVacationDaysAvailable && vacationAvailableDays !== null && newStartDate && requestForm.endDate) {
                    const start = moment(newStartDate);
                    const end = moment(requestForm.endDate);
                    const requestedDays = end.diff(start, 'days') + 1;
                    const maxDays = Math.floor(vacationAvailableDays);
                    if (maxDays > 0 && requestedDays > maxDays) {
                      const maxEndDate = start.clone().add(maxDays - 1, 'days');
                      setRequestForm(prev => ({
                        ...prev,
                        startDate: newStartDate,
                        endDate: maxEndDate.format(requestForm.specifyTimes ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
                      }));
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
                error={exceedsVacationAllowance}
                helperText={
                  exceedsVacationAllowance
                    ? canUseProjectedDays && projectedAvailableDays !== null
                      ? `You can request up to ${projectedAvailableDays.toFixed(1)} days (projected by anniversary). Current: ${(vacationAvailableDays ?? 0).toFixed(1)} days.`
                      : `You can only request up to ${(vacationAvailableDays ?? 0).toFixed(1)} days. Please adjust your date range.`
                    : canUseProjectedDays && projectedAvailableDays !== null && requestedVacationDays !== null && vacationAvailableDays !== null && requestedVacationDays > vacationAvailableDays
                      ? `Using projected days: ~${projectedAvailableDays.toFixed(1)} days available by anniversary (${moment(vacationBalance?.nextAnniversaryDate).format('MMM DD, YYYY')})`
                      : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={requestForm.specifyTimes ? "End Date & Time" : "End Date"}
                type={requestForm.specifyTimes ? "datetime-local" : "date"}
                value={requestForm.endDate}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  setRequestForm({ ...requestForm, endDate: newEndDate });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: requestForm.startDate || undefined
                }}
                error={exceedsVacationAllowance}
                helperText={
                  exceedsVacationAllowance
                    ? canUseProjectedDays && projectedAvailableDays !== null
                      ? `You can request up to ${projectedAvailableDays.toFixed(1)} days (projected by anniversary). Current: ${(vacationAvailableDays ?? 0).toFixed(1)} days.`
                      : `You can only request up to ${(vacationAvailableDays ?? 0).toFixed(1)} days. Please adjust your date range.`
                    : canUseProjectedDays && projectedAvailableDays !== null && requestedVacationDays !== null && vacationAvailableDays !== null && requestedVacationDays > vacationAvailableDays
                      ? `Using projected days: ~${projectedAvailableDays.toFixed(1)} days available by anniversary (${moment(vacationBalance?.nextAnniversaryDate).format('MMM DD, YYYY')})`
                      : undefined
                }
              />
            </Grid>
            {selectedDayCount !== null && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedDayCount === 1 
                    ? `Selected duration: 1 workday` 
                    : `Selected duration: ${selectedDayCount} workdays`}
                  {workdays.length === 0 && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      ⚠️ Please select at least one workday below
                    </Typography>
                  )}
                </Typography>
              </Grid>
            )}
            
            {/* Projected Days Info Alert */}
            {requestForm.type === 'vacation' && canUseProjectedDays && projectedAvailableDays !== null && vacationBalance?.nextAnniversaryDate && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    📅 Projected Vacation Days Available
                  </Typography>
                  <Typography variant="body2">
                    Your request is scheduled for on or after your anniversary date ({moment(vacationBalance.nextAnniversaryDate).format('MMM DD, YYYY')}).
                    You currently have <strong>{vacationAvailableDays?.toFixed(1) || 0} days</strong> available.
                    By your anniversary, you are projected to have approximately <strong>{projectedAvailableDays.toFixed(1)} days</strong> available.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Your request qualifies for projected days calculation based on your anniversary date.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {requestForm.type === 'vacation' && isPolicyAdvanceWarning && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    ⚠️ Policy Reminder
                  </Typography>
                  <Typography variant="body2">
                    Policy requires vacation requests to be submitted at least 30 days in advance.
                    You can still submit this request, but it is less than 30 days out. Request will be submitted to approbal process.
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            {/* Workdays Selection Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 2,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  Workdays for Time Off
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Select the days of the week you typically work. Only these days will be counted in your time-off request.
                </Typography>
                {loadingWorkdays ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading workdays from schedule...
                  </Typography>
                ) : (
                  <Grid container spacing={1}>
                    {[
                      { day: 1, label: 'Monday', short: 'Mon' },
                      { day: 2, label: 'Tuesday', short: 'Tue' },
                      { day: 3, label: 'Wednesday', short: 'Wed' },
                      { day: 4, label: 'Thursday', short: 'Thu' },
                      { day: 5, label: 'Friday', short: 'Fri' },
                      { day: 6, label: 'Saturday', short: 'Sat' },
                      { day: 0, label: 'Sunday', short: 'Sun' }
                    ].map(({ day, label, short }) => (
                      <Grid item xs={6} sm={4} md={3} key={day}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={workdays.includes(day)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setWorkdays([...workdays, day].sort());
                                } else {
                                  setWorkdays(workdays.filter(d => d !== day));
                                }
                              }}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {short}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Absence Type</InputLabel>
                <Select
                  value={requestForm.type}
                  onChange={(e) => setRequestForm({ ...requestForm, type: e.target.value })}
                >
                  {absenceTypes.map((type) => {
                    // Allow vacation if projected days are eligible, even if current balance is 0
                    const canUseProjectedVacation =
                      type === 'vacation' &&
                      canUseProjectedDaysForSelection &&
                      projectedAvailableDays !== null &&
                      projectedAvailableDays > 0;
                    const isVacationDisabled =
                      type === 'vacation' &&
                      (
                        (vacationAvailableDays === null) ||
                        (vacationAvailableDays <= 0 && !canUseProjectedVacation)
                      );

                    return (
                      <MenuItem
                        key={type}
                        value={type}
                        disabled={!!isVacationDisabled}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                        {isVacationDisabled && ' (No days available)'}
                        {!isVacationDisabled && canUseProjectedVacation && ' (Projected by anniversary)'}
                      </MenuItem>
                    );
                  })}
                </Select>
                {requestForm.type === 'vacation' && (
                  <Typography
                    variant="caption"
                    color={hasVacationDaysAvailable ? 'text.secondary' : 'error'}
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {(() => {
                      // Debug: Log what value is being used for the message
                      console.log('📝 Rendering vacation message with:', {
                        vacationAvailableDays,
                        vacationBalance: vacationBalance?.availableDays,
                        hasVacationDaysAvailable,
                        timestamp: new Date().toISOString()
                      });
                      
                      if (vacationAvailableDays === null) {
                        return 'Vacation balance information is unavailable. Please contact your supervisor.';
                      } else if (hasVacationDaysAvailable) {
                        if (vacationAvailableDays >= 1) {
                          let message = `You have ${vacationAvailableDays.toFixed(1)} vacation days available. You can request up to ${Math.floor(vacationAvailableDays)} days.`;
                          if (canUseProjectedDays && projectedAvailableDays !== null) {
                            message += ` If your request is scheduled on/after your anniversary (${moment(vacationBalance?.nextAnniversaryDate).format('MMM DD, YYYY')}), you may be able to request up to ${Math.floor(projectedAvailableDays)} days (projected).`;
                          }
                          return message;
                        } else {
                          return `You have ${vacationAvailableDays.toFixed(1)} vacation days available. Use "Specify Times" for partial-day vacation requests.`;
                        }
                      } else {
                        let message = 'You have no vacation days available. Please select "Personal" for time off requests.';
                        if (canUseProjectedDays && projectedAvailableDays !== null && projectedAvailableDays > 0) {
                          message += ` However, if your request is scheduled on/after your anniversary (${moment(vacationBalance?.nextAnniversaryDate).format('MMM DD, YYYY')}), you may be able to request up to ${Math.floor(projectedAvailableDays)} days (projected).`;
                        }
                        return message;
                      }
                    })()}
                  </Typography>
                )}
                {requestForm.type === 'personal' && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ mt: 0.5, display: 'block', fontWeight: 'medium' }}
                  >
                    ⚠️ Personal time off requests are not paid by the company. Only vacation days are paid.
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason (Optional)"
                multiline
                rows={3}
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateRequest}
            variant="contained"
            disabled={
              !requestForm.startDate || 
              !requestForm.endDate || 
              !requestForm.type ||
              workdays.length === 0 ||
              (requestForm.type === 'vacation' ? exceedsVacationAllowance : false)
            }
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalForm.action === 'approve' ? 'Approve' : 'Reject'} Request
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Employee: {selectedRequest.user?.firstName} {selectedRequest.user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {selectedRequest.type.replace('_', ' ').toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {formatDateRange(selectedRequest.startDate, selectedRequest.endDate, selectedRequest.timezone)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Reason (Required)"
            multiline
            rows={3}
            value={approvalForm.reason}
            onChange={(e) => setApprovalForm({ ...approvalForm, reason: e.target.value })}
            required
            error={!approvalForm.reason || !approvalForm.reason.trim()}
            helperText={(!approvalForm.reason || !approvalForm.reason.trim()) ? 'Please provide a reason for this decision' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApproveRequest}
            variant="contained"
            color={approvalForm.action === 'approve' ? 'success' : 'error'}
            disabled={!approvalForm.reason || !approvalForm.reason.trim()}
          >
            {approvalForm.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Approved Request Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Approved Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Employee: {selectedRequest.user?.firstName} {selectedRequest.user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {selectedRequest.type.replace('_', ' ').toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {formatDateRange(selectedRequest.startDate, selectedRequest.endDate, selectedRequest.timezone)}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={rejectForm.department}
              onChange={(e) => setRejectForm({ ...rejectForm, department: e.target.value })}
            >
              <MenuItem value="operations">Operations</MenuItem>
              <MenuItem value="administration">Administration</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectForm.reason}
            onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRejectRequest}
            variant="contained"
            color="error"
            disabled={!rejectForm.reason.trim()}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rejection Notes</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              {selectedRequest.administrationReason && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                    Administration Department
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedRequest.administrationReason}
                  </Typography>
                  {selectedRequest.administrationApprover && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Rejected by: {selectedRequest.administrationApprover.firstName} {selectedRequest.administrationApprover.lastName}
                      {selectedRequest.approvedByAdministrationAt && 
                        ` on ${moment(selectedRequest.approvedByAdministrationAt).format('MMM DD, YYYY')}`
                      }
                    </Typography>
                  )}
                </Box>
              )}
              {selectedRequest.operationsReason && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                    Operations Department
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedRequest.operationsReason}
                  </Typography>
                  {selectedRequest.operationsApprover && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Rejected by: {selectedRequest.operationsApprover.firstName} {selectedRequest.operationsApprover.lastName}
                      {selectedRequest.approvedByOperationsAt && 
                        ` on ${moment(selectedRequest.approvedByOperationsAt).format('MMM DD, YYYY')}`
                      }
                    </Typography>
                  )}
                </Box>
              )}
              {!selectedRequest.administrationReason && !selectedRequest.operationsReason && (
                <Typography variant="body2" color="text.secondary">
                  No rejection notes available.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit & Resubmit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit & Resubmit Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requestForm.specifyTimes}
                    onChange={(e) => {
                      setRequestForm({ ...requestForm, specifyTimes: e.target.checked });
                    }}
                  />
                }
                label="Specify times (instead of all day)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type={requestForm.specifyTimes ? "datetime-local" : "date"}
                value={requestForm.startDate}
                onChange={(e) => setRequestForm({ ...requestForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type={requestForm.specifyTimes ? "datetime-local" : "date"}
                value={requestForm.endDate}
                onChange={(e) => setRequestForm({ ...requestForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={requestForm.type}
                  onChange={(e) => setRequestForm({ ...requestForm, type: e.target.value })}
                >
                  {absenceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason (Optional)"
                multiline
                rows={3}
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResubmitRequest}
            variant="contained"
            disabled={!requestForm.startDate || !requestForm.endDate || !requestForm.type || loading}
          >
            {loading ? 'Resubmitting...' : 'Resubmit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimeAbsenceRequestsPage;
